/**
 * Checkbox UI primitive — styled, accessible, controlled.
 *
 * US-002 / AC-1 / AC-2: Provides the T&C consent checkbox gate on RegisterPage.
 * No @radix-ui dependency is used (none installed in package.json); this is a
 * lightweight native input[type=checkbox] primitive styled with otter design tokens,
 * reusing cn() from @/lib/utils and the React.forwardRef + InputHTMLAttributes
 * pattern from input.tsx exactly.
 *
 * WCAG 2.1 AA: focus ring visible via focus-visible:ring-2, keyboard-focusable via
 * native input, Label htmlFor association supported.
 * TO-LL-044: component is controlled (accepts checked + onCheckedChange props);
 * parent owns state (no internal global state).
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  /** Whether the checkbox is checked (controlled). */
  checked?: boolean;
  /** Callback fired when the checked state changes. */
  onCheckedChange?: (checked: boolean) => void;
}

/**
 * Controlled Checkbox primitive styled with otter design tokens.
 * Renders a native input[type=checkbox] with otter-orange accent and focus ring.
 * Accepts a 44px-min touch target on mobile via the min-h-[44px] container sizing.
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, checked, onCheckedChange, disabled, id, ...props }, ref) => {
    return (
      <input
        type="checkbox"
        id={id}
        ref={ref}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCheckedChange?.(e.target.checked)}
        className={cn(
          // Base sizing — h-5 w-5 for visual display; min-h-[44px] touch area on mobile
          'h-5 w-5 rounded border border-[#E7D8C8] bg-white',
          // Otter-orange checked accent (CSS accent-color, works in modern browsers)
          'accent-[#EA6A2A]',
          // Focus ring for keyboard accessibility (WCAG 2.1 AA)
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EA6A2A] focus-visible:ring-offset-2',
          // Disabled state mirrors Input disabled styling
          'disabled:cursor-not-allowed disabled:opacity-50',
          // Cursor pointer when interactive
          'cursor-pointer',
          className
        )}
        {...props}
      />
    );
  }
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
